/**
 * Transition Rules: EnergyPlus 23.2 to 24.1
 */

import type { VersionTransition, ObjectTransitionRule } from '../types';
import type { IDFObject } from '../../parser/types';
import { createCopyMappings, createShiftedCopyMappings } from '../types';

export function createTransition_23_2_to_24_1(): VersionTransition {
  const objectRules = new Map<string, ObjectTransitionRule>();
  const variableMappings = new Map<string, string>();
  const deletedObjects = new Set<string>();
  const renamedObjects = new Map<string, string>();

  // AirLoopHVAC:UnitarySystem - Add field 39 based on coil types
  // If field 12 is Coil:Heating:DX:VariableSpeed OR field 15 is Coil:Cooling:DX:VariableSpeed → 'Yes'
  objectRules.set('AIRLOOPHVAC:UNITARYSYSTEM', {
    sourceClass: 'AirLoopHVAC:UnitarySystem',
    targetClass: 'AirLoopHVAC:UnitarySystem',
    fieldMappings: [
      ...createCopyMappings(0, 38), // Fields 0-37
      {
        sourceIndex: -1,
        targetIndex: 38,
        type: 'add_default',
        defaultValue: 'No', // Will be overridden by postprocess
      },
      ...createShiftedCopyMappings(38, 39, 30),
    ],
    postprocess: (obj: IDFObject) => {
      const heatingCoilType = obj.fields[11]?.value.toUpperCase() || '';
      const coolingCoilType = obj.fields[14]?.value.toUpperCase() || '';
      const isVariableSpeed =
        heatingCoilType.includes('VARIABLESPEED') ||
        coolingCoilType.includes('VARIABLESPEED');
      if (obj.fields[38]) {
        obj.fields[38].value = isVariableSpeed ? 'Yes' : 'No';
      }
      return obj;
    },
  });

  // ComfortViewFactorAngles - Delete field 2
  objectRules.set('COMFORTVIEWFACTORANGLES', {
    sourceClass: 'ComfortViewFactorAngles',
    targetClass: 'ComfortViewFactorAngles',
    fieldMappings: [
      { sourceIndex: 0, targetIndex: 0, type: 'copy' },
      { sourceIndex: 1, targetIndex: -1, type: 'delete' },
      ...createShiftedCopyMappings(2, 1, 50),
    ],
  });

  // People - Rename 'ZoneAveraged' to 'EnclosureAveraged' in field 13
  objectRules.set('PEOPLE', {
    sourceClass: 'People',
    targetClass: 'People',
    fieldMappings: [
      ...createCopyMappings(0, 12), // Fields 0-11
      {
        sourceIndex: 12,
        targetIndex: 12,
        type: 'transform',
        transform: (value) =>
          value.toUpperCase() === 'ZONEAVERAGED' ? 'EnclosureAveraged' : value,
      },
      ...createShiftedCopyMappings(13, 13, 30),
    ],
  });

  // ZoneHVAC:PackagedTerminalAirConditioner - Add field 10 based on coil type
  objectRules.set('ZONEHVAC:PACKAGEDTERMINALAIRCONDITIONER', {
    sourceClass: 'ZoneHVAC:PackagedTerminalAirConditioner',
    targetClass: 'ZoneHVAC:PackagedTerminalAirConditioner',
    fieldMappings: [
      ...createCopyMappings(0, 9), // Fields 0-8
      {
        sourceIndex: -1,
        targetIndex: 9,
        type: 'add_default',
        defaultValue: 'No',
      },
      ...createShiftedCopyMappings(9, 10, 20),
    ],
    postprocess: (obj: IDFObject) => {
      const coolingCoilType = obj.fields[16]?.value.toUpperCase() || '';
      const isVariableSpeed = coolingCoilType.includes('VARIABLESPEED');
      if (obj.fields[9]) {
        obj.fields[9].value = isVariableSpeed ? 'Yes' : 'No';
      }
      return obj;
    },
  });

  // ZoneHVAC:PackagedTerminalHeatPump - Add field 10 based on coil types
  objectRules.set('ZONEHVAC:PACKAGEDTERMINALHEATPUMP', {
    sourceClass: 'ZoneHVAC:PackagedTerminalHeatPump',
    targetClass: 'ZoneHVAC:PackagedTerminalHeatPump',
    fieldMappings: [
      ...createCopyMappings(0, 9), // Fields 0-8
      {
        sourceIndex: -1,
        targetIndex: 9,
        type: 'add_default',
        defaultValue: 'No',
      },
      ...createShiftedCopyMappings(9, 10, 30),
    ],
    postprocess: (obj: IDFObject) => {
      const heatingCoilType = obj.fields[14]?.value.toUpperCase() || '';
      const coolingCoilType = obj.fields[17]?.value.toUpperCase() || '';
      const isVariableSpeed =
        heatingCoilType.includes('VARIABLESPEED') ||
        coolingCoilType.includes('VARIABLESPEED');
      if (obj.fields[9]) {
        obj.fields[9].value = isVariableSpeed ? 'Yes' : 'No';
      }
      return obj;
    },
  });

  // ZoneHVAC:WaterToAirHeatPump - Add field 10 (always 'No')
  objectRules.set('ZONEHVAC:WATERTOAIRHEATPUMP', {
    sourceClass: 'ZoneHVAC:WaterToAirHeatPump',
    targetClass: 'ZoneHVAC:WaterToAirHeatPump',
    fieldMappings: [
      ...createCopyMappings(0, 9), // Fields 0-8
      { sourceIndex: -1, targetIndex: 9, type: 'add_default', defaultValue: 'No' },
      ...createShiftedCopyMappings(9, 10, 30),
    ],
  });

  // Note: HeatExchanger:AirToAir:SensibleAndLatent has a complex transformation
  // that creates Table:Lookup objects when effectiveness values differ.
  // This would require multi-object output support.

  return {
    fromVersion: { major: 23, minor: 2 },
    toVersion: { major: 24, minor: 1 },
    objectRules,
    variableMappings,
    deletedObjects,
    renamedObjects,
  };
}
